/**
 * Safe Web Storage access.
 *
 * Why this exists:
 *   When a WebView (or browser) is configured to block all cookies / site data,
 *   merely *accessing* `window.localStorage` / `window.sessionStorage` throws a
 *   `SecurityError` ("Access is denied for this document."). The same happens
 *   for IndexedDB-backed APIs. If such a throw happens at module-load time in
 *   the synchronous bootstrap import chain, it aborts the chain before React
 *   mounts → blank/white screen. This was the root cause of the "deny cookies =
 *   blank screen" reports. See docs/white-screen-investigation.md.
 *
 *   Every storage touchpoint that can run during bootstrap MUST go through these
 *   helpers (or otherwise guard access) so a blocked-storage environment
 *   degrades gracefully instead of taking the whole app down.
 *
 * Behaviour:
 *   - getStorage() returns the real Storage when available, otherwise an
 *     in-memory fallback that satisfies the Storage interface. It NEVER throws.
 *   - The convenience get/set/remove helpers never throw either.
 */

/** Minimal in-memory Storage shim used when real storage is unavailable. */
function createMemoryStorage(): Storage {
  const map = new Map<string, string>()
  return {
    get length() {
      return map.size
    },
    clear() {
      map.clear()
    },
    getItem(key: string) {
      return map.has(key) ? (map.get(key) as string) : null
    },
    key(index: number) {
      return Array.from(map.keys())[index] ?? null
    },
    removeItem(key: string) {
      map.delete(key)
    },
    setItem(key: string, value: string) {
      map.set(key, String(value))
    },
  } as Storage
}

/**
 * Return a usable Storage for the given kind. Accessing the real storage object
 * is wrapped in try/catch because the *property access itself* throws when
 * cookies/site-data are blocked. A probe write confirms it's actually writable
 * (some engines expose the object but throw on write in private/blocked modes).
 * Falls back to an in-memory store that never throws.
 */
function resolveStorage(kind: 'localStorage' | 'sessionStorage'): Storage {
  try {
    if (typeof window === 'undefined') return createMemoryStorage()
    const store = window[kind]
    if (!store) return createMemoryStorage()
    // Probe: confirm read/write works (throws here in blocked WebViews).
    const probe = '__oister_storage_probe__'
    store.setItem(probe, '1')
    store.removeItem(probe)
    return store
  } catch {
    return createMemoryStorage()
  }
}

/**
 * Returns a never-throwing localStorage. Prefer this over `window.localStorage`
 * anywhere that may run during bootstrap. Resolved lazily on each call so a
 * later consent change that re-enables storage is picked up.
 */
export function getSafeLocalStorage(): Storage {
  return resolveStorage('localStorage')
}

/** Returns a never-throwing sessionStorage. See getSafeLocalStorage. */
export function getSafeSessionStorage(): Storage {
  return resolveStorage('sessionStorage')
}

export function safeLocalStorageGet(key: string): string | null {
  try {
    return getSafeLocalStorage().getItem(key)
  } catch {
    return null
  }
}

export function safeLocalStorageSet(key: string, value: string): void {
  try {
    getSafeLocalStorage().setItem(key, value)
  } catch {
    /* ignore — storage unavailable */
  }
}

export function safeLocalStorageRemove(key: string): void {
  try {
    getSafeLocalStorage().removeItem(key)
  } catch {
    /* ignore — storage unavailable */
  }
}
